import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { POSTS, formatPostDate } from "@/content/blog";
import { aspectSize } from "@/lib/img";
import Seo, { BREADCRUMB_JSONLD, COLLECTION_PAGE_JSONLD } from "@/components/Seo";

export default function Blog() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [lead, ...rest] = POSTS;

  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-12 md:py-20">
      <Seo
        title="Journal: Jewellery CAD Guides and Notes"
        description="Notes on jewellery CAD, working with the studio, and turning an idea into a production-ready file — from the GetJewelDesigns team."
        path="/blog"
        jsonLd={[COLLECTION_PAGE_JSONLD("/blog", "Journal — GetJewelDesigns"), BREADCRUMB_JSONLD([{ name: "Home", path: "/" }, { name: "Journal", path: "/blog" }])]}
      />
      <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">Journal</div>
      <h1 className="font-serif text-4xl md:text-6xl mb-4 tracking-tight">The GetJewelDesigns journal.</h1>
      <p className="text-muted-foreground text-lg leading-relaxed max-w-2xl mb-12 md:mb-16">
        Notes on jewellery CAD, working with the studio, and turning an idea into a production-ready file.
      </p>

      {lead && (
        <Link
          to={`/blog/${lead.slug}`}
          data-testid={`blog-card-${lead.slug}`}
          className="group grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 border-b hairline pb-12 md:pb-16 mb-12 md:mb-16"
        >
          <div className="aspect-[16/10] overflow-hidden border hairline">
            <img src={lead.cover} {...aspectSize("16/10", 1200)} alt={lead.title} fetchPriority="high" className="w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-80" />
          </div>
          <div className="flex flex-col justify-center">
            <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground mb-4">
              <span className="text-primary">{lead.tag}</span>
              <span>·</span>
              <span>{formatPostDate(lead.date)}</span>
              <span>·</span>
              <span>{lead.readingTime}</span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl mb-4 leading-tight group-hover:text-primary transition-colors duration-300">{lead.title}</h2>
            <p className="text-muted-foreground leading-relaxed max-w-xl">{lead.excerpt}</p>
          </div>
        </Link>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
        {rest.map((p) => (
          <Link
            key={p.slug}
            to={`/blog/${p.slug}`}
            data-testid={`blog-card-${p.slug}`}
            className="group block"
          >
            <div className="aspect-[16/11] overflow-hidden border hairline mb-5">
              <img src={p.cover} {...aspectSize("16/11", 1200)} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-80" />
            </div>
            <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground mb-3">
              <span className="text-primary">{p.tag}</span>
              <span>·</span>
              <span>{p.readingTime}</span>
            </div>
            <h3 className="font-serif text-2xl mb-2 leading-tight group-hover:text-primary transition-colors duration-300">{p.title}</h3>
            <p className="text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
